/**
 * Shadow-log helpers — let Head Coaches "View as" another staff role.
 *
 * Why: Quickly preview what an Assistant Coach / Athletic Trainer / Strength Coach
 * would see. Sets `armiq_view_as` in sessionStorage and reloads to reflect.
 *
 * IMPORTANT: This is a UI-only mask. Backend permissions still apply if a
 * head_coach uses an API directly. We only hide controls visually so heads
 * can spot information leaks before they hit production.
 */
import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "armiq_view_as";

export const COACH_ROLES = ["head_coach", "pitching_coach", "assistant_coach", "athletic_trainer", "strength_coach"];

export const ROLE_LABELS = {
  head_coach: "Head Coach",
  pitching_coach: "Pitching Coach",
  assistant_coach: "Assistant Coach",
  athletic_trainer: "Athletic Trainer",
  strength_coach: "Strength Coach",
};

// canonical permissions matrix mirroring /app/backend/permissions.py
const PERMS = {
  head_coach:       new Set(["edit_calendar", "edit_weights", "edit_roster", "edit_branding", "manage_staff", "view_biometrics", "override"]),
  pitching_coach:   new Set(["edit_calendar", "edit_weights", "edit_roster", "edit_branding", "manage_staff", "view_biometrics"]),
  assistant_coach:  new Set(["edit_roster", "view_biometrics"]),
  athletic_trainer: new Set(["edit_roster", "view_biometrics"]),
  strength_coach:   new Set(["edit_weights", "view_biometrics"]),
};

export function getViewAs() {
  if (typeof window === "undefined") return null;
  return sessionStorage.getItem(STORAGE_KEY) || null;
}

export function setViewAs(role) {
  if (!role) sessionStorage.removeItem(STORAGE_KEY);
  else sessionStorage.setItem(STORAGE_KEY, role);
  window.dispatchEvent(new Event("armiq:viewas"));
}

/**
 * Returns the role currently being used for permission decisions.
 * - admin → admin (cannot be shadowed)
 * - non-coach → user's actual role
 * - coach with no view-as → user.team_role (defaults pitching_coach)
 * - head_coach with view-as → the shadowed role
 */
export function useEffectiveRole() {
  const { user } = useAuth();
  const [viewAs, setVa] = useState(getViewAs());
  useEffect(() => {
    const update = () => setVa(getViewAs());
    window.addEventListener("armiq:viewas", update);
    return () => window.removeEventListener("armiq:viewas", update);
  }, []);
  if (!user) return { role: null, isShadow: false, real: null, set: setViewAs };
  if (user.role !== "coach") return { role: user.team_role || null, isShadow: false, real: user.team_role || null, set: setViewAs };
  const real = user.team_role || "pitching_coach";
  const canShadow = real === "head_coach";
  const effective = canShadow && viewAs && COACH_ROLES.includes(viewAs) && viewAs !== real ? viewAs : real;
  return { role: effective, isShadow: effective !== real, real, set: setViewAs };
}

export function useCan(permission) {
  const { user } = useAuth();
  const { role } = useEffectiveRole();
  if (!user) return false;
  if (user.role === "admin") return true;
  if (user.role !== "coach") return false;
  return PERMS[role]?.has(permission) || false;
}
